import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Chip,
  Stack,
  Divider,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { fetchServiceSettings, updateServiceSettings } from '../../api/hotelinfoApi';

const DEFAULT_POLICY = {
  allowed: false,
  windowHours: 24,
  feeType: 'none',
  feeValue: 0,
  notes: '',
};

const DEFAULT_SETTINGS = {
  tax: { rate: 0, type: 'percentage' },
  paymentTypes: [],
  cancellationPolicy: { ...DEFAULT_POLICY },
};

/**
 * ServiceSetting.jsx — Page version of ServiceSettingsModal
 * - Fetches and saves settings at `${API_BASE}/api/service-settings/:propertyId`
 * - Reuses the same field names (`settings.tax`, `settings.paymentTypes`, etc.)
 * - Navigates back on Cancel or after successful Save (optional)
 */
export default function ServiceSetting() {
  const navigate = useNavigate();
  const location = useLocation();

  // Try to discover propertyId the same way the rest of the app does
  const routePropertyId = (location.state && location.state.propertyId) || undefined;
  const storedUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
  }, []);
  const fallbackPropertyId = storedUser?.propertyId?._id || storedUser?.propertyId || undefined;
  const propertyId = routePropertyId || fallbackPropertyId;

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [initialSettings, setInitialSettings] = useState(DEFAULT_SETTINGS);
  const [newPaymentType, setNewPaymentType] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedToast, setSavedToast] = useState(false);

  const dirty = useMemo(
    () => JSON.stringify(settings) !== JSON.stringify(initialSettings),
    [settings, initialSettings]
  );

  useEffect(() => {
    let ignore = false;
    async function fetchSettings() {
      if (!propertyId) { setLoading(false); return; }
      setLoading(true);
      try {
        const data = await fetchServiceSettings(propertyId);
        if (!ignore && data) {
          const merged = {
            ...DEFAULT_SETTINGS,
            ...data,
            tax: { ...DEFAULT_SETTINGS.tax, ...(data.tax || {}) },
            paymentTypes: data.paymentTypes || [],
            cancellationPolicy: {
              ...DEFAULT_POLICY,
              ...(data.cancellationPolicy || {}),
            },
          };
          setSettings(merged);
          setInitialSettings(merged);
        }
      } catch (e) {
        if (!ignore) setError('Failed to load settings');
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    fetchSettings();
    return () => { ignore = true; };
  }, [propertyId]);

  const handleTaxChange = (e) => {
    const { name, value } = e.target;
    setSettings(s => ({ ...s, tax: { ...s.tax, [name]: value } }));
  };

  const handleAddPaymentType = () => {
    const t = newPaymentType.trim();
    if (!t) return;
    setSettings(s => (
      s.paymentTypes?.includes(t)
        ? s
        : { ...s, paymentTypes: [...(s.paymentTypes || []), t] }
    ));
    setNewPaymentType('');
  };

  const handleDeletePaymentType = (t) => {
    setSettings(s => ({ ...s, paymentTypes: (s.paymentTypes || []).filter(x => x !== t) }));
  };

  const handleCancellationToggle = (event) => {
    const allowed = event.target.checked;
    setSettings(s => ({
      ...s,
      cancellationPolicy: {
        ...s.cancellationPolicy,
        allowed,
      },
    }));
  };

  const handleCancellationInput = (field) => (event) => {
    let value = event.target.value;

    if (field === 'windowHours' || field === 'feeValue') {
      const parsed = Number(value);
      value = Number.isNaN(parsed) ? 0 : Math.max(0, parsed);
    }

    setSettings(s => ({
      ...s,
      cancellationPolicy: {
        ...s.cancellationPolicy,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    if (!propertyId) { setError('Missing propertyId'); return; }
    setSaving(true);
    try {
      const updated = await updateServiceSettings(propertyId, settings);
      const next = { ...settings, ...(updated || {}) };
      setSettings(next);
      setInitialSettings(next);
      setSavedToast(true);
      // navigate(-1); // uncomment if you want to go back after save
    } catch (e) {
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          backgroundColor: 'background.paper',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={600}>Service Settings</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure service tax & payment preferences
          </Typography>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Tax Section */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Tax Settings</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Tax Rate (%)"
            name="rate"
            type="number"
            fullWidth
            value={settings.tax?.rate ?? 0}
            onChange={handleTaxChange}
          />
          <FormControl fullWidth>
            <InputLabel>Tax Type</InputLabel>
            <Select
              label="Tax Type"
              name="type"
              value={settings.tax?.type || 'percentage'}
              onChange={handleTaxChange}
              MenuProps={{
                PaperProps: {
                  elevation: 4,
                  sx: {
                    boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
                    borderRadius: 2,
                    mt: 1,
                  },
                },
              }}
            >
              <MenuItem value="percentage">Percentage (%)</MenuItem>
              <MenuItem value="fixed">Fixed Amount</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Payment Types Section */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Payment Options</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            placeholder="Add new payment method..."
            value={newPaymentType}
            onChange={(e) => setNewPaymentType(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddPaymentType()}
            fullWidth
          />
          <IconButton
            aria-label="Add payment type"
            onClick={handleAddPaymentType}
            color="primary"
            sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            <Plus size={18} />
          </IconButton>
        </Stack>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
          {(settings.paymentTypes || []).map((t) => (
            <Chip
              key={t}
              label={t}
              onDelete={() => handleDeletePaymentType(t)}
              sx={{ bgcolor: 'grey.100', '& .MuiChip-deleteIcon': { color: 'text.secondary' }, '&:hover': { bgcolor: 'grey.200' } }}
            />
          ))}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Cancellation Policy Section */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Cancellation Policy</Typography>
        <FormControlLabel
          control={(
            <Switch
              checked={settings.cancellationPolicy?.allowed || false}
              onChange={handleCancellationToggle}
            />
          )}
          label="Allow guests to cancel their bookings"
          sx={{ mb: 2 }}
        />

        {settings.cancellationPolicy?.allowed && (
          <Stack spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Cancellation window (hours before)"
              type="number"
              value={settings.cancellationPolicy?.windowHours ?? DEFAULT_POLICY.windowHours}
              onChange={handleCancellationInput('windowHours')}
              inputProps={{ min: 0 }}
            />
            <FormControl fullWidth>
              <InputLabel>Cancellation fee type</InputLabel>
              <Select
                label="Cancellation fee type"
                value={settings.cancellationPolicy?.feeType || DEFAULT_POLICY.feeType}
                onChange={handleCancellationInput('feeType')}
              >
                <MenuItem value="none">No fee</MenuItem>
                <MenuItem value="percentage">Percentage of order total</MenuItem>
                <MenuItem value="fixed">Fixed amount</MenuItem>
              </Select>
            </FormControl>
            {settings.cancellationPolicy?.feeType !== 'none' && (
              <TextField
                label={settings.cancellationPolicy?.feeType === 'percentage' ? 'Fee (%)' : 'Fee amount'}
                type="number"
                value={settings.cancellationPolicy?.feeValue ?? DEFAULT_POLICY.feeValue}
                onChange={handleCancellationInput('feeValue')}
                inputProps={{ min: 0 }}
              />
            )}
            <TextField
              label="Additional notes"
              value={settings.cancellationPolicy?.notes || ''}
              onChange={handleCancellationInput('notes')}
              multiline
              minRows={3}
            />
          </Stack>
        )}

        <Divider sx={{ my: 3 }} />

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button variant="outlined" onClick={() => navigate(-1)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!dirty || saving}
            sx={{
              px: 3,
              textTransform: 'none',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
              bgcolor: !dirty ? 'grey.300' : 'primary.main',
              color: !dirty ? 'text.disabled' : 'white',
              '&:hover': { bgcolor: !dirty ? 'grey.400' : 'primary.dark' },
            }}
          >
            {saving ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} sx={{ color: 'inherit' }} />
                <span>Saving…</span>
              </Stack>
            ) : 'Save Changes'}
          </Button>
        </Stack>
      </Paper>

      <Snackbar
        open={savedToast}
        autoHideDuration={2000}
        onClose={() => setSavedToast(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSavedToast(false)}
          severity="success"
          sx={{
            width: '100%',
            backdropFilter: 'blur(16px)',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            color: '#2E7D32',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            fontWeight: 500,
          }}
        >
          Settings saved
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!error}
        autoHideDuration={2500}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setError('')}
          severity="error"
          sx={{
            width: '100%',
            backdropFilter: 'blur(16px)',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            color: '#D32F2F',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            fontWeight: 500,
          }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
}
